import React, { useState, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
  UserIcon,
  SignalIcon,
  SignalSlashIcon,
  GlobeAltIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CreditCardIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import UserCard from '../components/users/UserCard';
import axiosInstance from '../utils/axios';
import { PageHeader, StatCard } from '../components/common';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../config/permissions';

const Users = ({ users = [], subscribers = [] }) => {
  const { user: currentUser } = useAuth();
  const isDeveloper = hasRole(currentUser?.role, 'developer');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // Changed default from '' to 'active' to show only online users
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateResult, setDeactivateResult] = useState(null);

  // Combine users with subscriber data to determine online status
  const { usersWithStatus, onlineCount, statistics } = useMemo(() => {
    // Create a map of subscribers by userId for easy lookup
    const subscriberMap = new Map();
    subscribers.forEach(sub => {
      subscriberMap.set(sub.userId, sub);
    });

    // Update users with online status and subscriber data
    const withStatus = users.map(user => {
      const subscriber = subscriberMap.get(user._id);
      return {
        ...user,
        isOnline: !!subscriber,
        subscriberData: subscriber || null,
        // Add real-time connection info
        activeConnections: subscriber?.socketCount || 0,
        subscribedTopics: subscriber?.subscriptions || []
      };
    });

    // Count online users
    const online = withStatus.filter(u => u.isOnline).length;

    // Calculate comprehensive statistics
    const stats = {
      totalUsers: users.length,
      onlineUsers: online,
      offlineUsers: users.length - online,
      onlinePercentage: users.length > 0 ? ((online / users.length) * 100).toFixed(1) : 0,
      offlinePercentage: users.length > 0 ? (((users.length - online) / users.length) * 100).toFixed(1) : 0,
      activeUsers: users.filter(u => u.status === 'active').length,
      inactiveUsers: users.filter(u => u.status === 'inactive').length,
      activeSubscriptions: users.filter(u => u.subscription?.status === 'active' || u.subscription?.status === 'trial').length,
      inactiveSubscriptions: users.filter(u => !u.subscription?.status || (u.subscription?.status !== 'active' && u.subscription?.status !== 'trial')).length,
      operators: users.filter(u => u.role === 'operator').length,
      regularUsers: users.filter(u => u.role === 'user' || !u.role).length,
      usersWithDevices: users.filter(u => u.devices && u.devices.length > 0).length,
      newUsersThisMonth: users.filter(u => {
        const createdAt = new Date(u.createdAt);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return createdAt >= thirtyDaysAgo;
      }).length,
      countriesRepresented: [...new Set(users.map(u => u.country).filter(Boolean))].length,
      totalActiveConnections: withStatus.reduce((sum, u) => sum + u.activeConnections, 0)
    };

    return {
      usersWithStatus: withStatus,
      onlineCount: online,
      statistics: stats
    };
  }, [users, subscribers]);

  // Filter users based on search and filters
  const filteredUsers = useMemo(() => {
    return usersWithStatus.filter(user => {
      // Search filter - enhanced to include phone and address
      if (search) {
        const searchLower = search.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        
        // Check various fields for search match
        const matchFound = 
          fullName.includes(searchLower) ||
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.farmName?.toLowerCase().includes(searchLower) ||
          user.cellPhone?.toLowerCase().includes(searchLower) ||
          user.cellPhone?.replace(/\D/g, '').includes(search.replace(/\D/g, '')) || // Phone without formatting
          user.address?.city?.toLowerCase().includes(searchLower) ||
          user.address?.state?.toLowerCase().includes(searchLower) ||
          user.address?.country?.toLowerCase().includes(searchLower);
        
        if (!matchFound) return false;
      }

      // Role filter
      if (roleFilter && user.role !== roleFilter) {
        return false;
      }

      // Status filter
      if (statusFilter === 'active' && !user.isOnline) return false;
      if (statusFilter === 'inactive' && user.isOnline) return false;

      return true;
    });
  }, [usersWithStatus, search, roleFilter, statusFilter]);

  // Handle ending annual access for every customer at once
  const handleDeactivateAll = async () => {
    setIsDeactivating(true);
    try {
      const response = await axiosInstance.post('/v1/users/deactivate-all-subscriptions');

      if (response.data.success) {
        setDeactivateResult({
          success: true,
          message: response.data.message,
          data: response.data.data
        });

        // Refresh the page after 3 seconds to show updated data
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
    } catch (error) {
      setDeactivateResult({
        success: false,
        message: error.response?.data?.message || 'Failed to end annual access',
        error: error.message
      });
    } finally {
      setIsDeactivating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        subtitle="Monitor and manage your platform users"
        actions={
          isDeveloper && (
            <button
              onClick={() => setShowDeactivateModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
            >
              <ExclamationTriangleIcon className="h-5 w-5" />
              Deactivate All Mobile Access
            </button>
          )
        }
      />

      {/* Enhanced Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
        <StatCard
          label="Total Users"
          value={statistics.totalUsers}
          icon={UserGroupIcon}
          delay={0.1}
        />
        <StatCard
          label="Online Now"
          value={statistics.onlineUsers}
          valueColor="text-green-600"
          subtitle={`${statistics.onlinePercentage}% of total`}
          icon={SignalIcon}
          iconColor="text-green-500"
          delay={0.15}
        />
        <StatCard
          label="Offline"
          value={statistics.offlineUsers}
          valueColor="text-gray-600"
          subtitle={`${statistics.offlinePercentage}% of total`}
          icon={SignalSlashIcon}
          iconColor="text-gray-500"
          delay={0.17}
        />
        <StatCard
          label="Annual Access"
          value={statistics.activeSubscriptions}
          valueColor="text-emerald-600"
          subtitle="Active"
          icon={CreditCardIcon}
          iconColor="text-emerald-500"
          delay={0.22}
        />
        <StatCard
          label="Active"
          value={statistics.activeUsers}
          valueColor="text-indigo-600"
          icon={UserIcon}
          iconColor="text-indigo-500"
          delay={0.26}
        />
        <StatCard
          label="New Users"
          value={statistics.newUsersThisMonth}
          valueColor="text-purple-600"
          subtitle="Last 30 days"
          icon={UserPlusIcon}
          iconColor="text-purple-500"
          delay={0.3}
        />
        <StatCard
          label="Countries"
          value={statistics.countriesRepresented}
          valueColor="text-orange-600"
          subtitle="Global reach"
          icon={GlobeAltIcon}
          iconColor="text-orange-500"
          delay={0.34}
        />
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, farm, email, phone, or location..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="operator">Operator</option>
            <option value="demo">Demo</option>
          </select>

          {/* Status Filter - Button Style */}
          <div className="flex gap-2">
            {[
              { value: '', label: 'All' },
              { value: 'active', label: 'Online' },
              { value: 'inactive', label: 'Offline' }
            ].map((status) => (
              <button
                key={status.value}
                onClick={() => setStatusFilter(status.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  statusFilter === status.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Search Results Info */}
      {(search || roleFilter || statusFilter) && (
        <div className="flex items-center justify-between px-4 py-2 bg-blue-50 rounded-lg">
          <div className="text-sm text-blue-700">
            Found <span className="font-semibold">{filteredUsers.length}</span> {filteredUsers.length === 1 ? 'user' : 'users'}
            {search && <span> matching "{search}"</span>}
            {roleFilter && <span> with role "{roleFilter}"</span>}
            {statusFilter && <span> ({statusFilter})</span>}
          </div>
          {(search || roleFilter || statusFilter) && (
            <button
              onClick={() => {
                setSearch('');
                setRoleFilter('');
                setStatusFilter('');
              }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Users Grid */}
      {filteredUsers.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No users found</p>
          <p className="text-gray-400 mt-2">
            {search || roleFilter || statusFilter ? 'Try adjusting your filters' : 'No users in the system'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredUsers.map((user, index) => (
              <motion.div 
                key={user._id} 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
              >
                <UserCard user={user} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Deactivate All Mobile Access Modal */}
      <AnimatePresence>
        {showDeactivateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget && !isDeactivating) {
                setShowDeactivateModal(false);
                setDeactivateResult(null);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              {!deactivateResult ? (
                // Warning/Confirmation View
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-3 bg-red-100 rounded-full">
                      <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Deactivate All Mobile Access</h3>
                      <p className="text-sm text-gray-600">This action cannot be undone</p>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-800 font-medium mb-2">⚠️ WARNING: This will:</p>
                    <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                      <li>End ALL customer annual access immediately</li>
                      <li>Affect {statistics.usersWithDevices} users with active devices</li>
                      <li>Require customers to pay again to regain access</li>
                      <li>This action is logged and cannot be reversed</li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Users without billing details will be automatically skipped.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowDeactivateModal(false);
                        setDeactivateResult(null);
                      }}
                      disabled={isDeactivating}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeactivateAll}
                      disabled={isDeactivating}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isDeactivating ? (
                        <>
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          Processing...
                        </>
                      ) : (
                        <>Confirm Deactivation</>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                // Result View
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-3 rounded-full ${
                      deactivateResult.success ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {deactivateResult.success ? (
                        <CheckBadgeIcon className="h-8 w-8 text-green-600" />
                      ) : (
                        <XCircleIcon className="h-8 w-8 text-red-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {deactivateResult.success ? 'Deactivation Complete' : 'Deactivation Failed'}
                      </h3>
                    </div>
                  </div>

                  <div className={`rounded-lg p-4 mb-4 ${
                    deactivateResult.success
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <p className={`text-sm ${
                      deactivateResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {deactivateResult.message}
                    </p>

                    {deactivateResult.success && deactivateResult.data && (
                      <div className="mt-3 space-y-1">
                        <p className="text-xs text-green-700">
                          • Total users: {deactivateResult.data.totalUsers}
                        </p>
                        <p className="text-xs text-green-700">
                          • Annual access ended: {deactivateResult.data.modifiedCount}
                        </p>
                        <p className="text-xs text-green-700">
                          • Users without billing skipped: {deactivateResult.data.skippedCount}
                        </p>
                      </div>
                    )}
                  </div>

                  {deactivateResult.success && (
                    <p className="text-sm text-gray-600 mb-4">
                      The page will refresh automatically in a few seconds...
                    </p>
                  )}

                  <button
                    onClick={() => {
                      setShowDeactivateModal(false);
                      setDeactivateResult(null);
                      if (deactivateResult.success) {
                        window.location.reload();
                      }
                    }}
                    className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Users;